import {useCallback, useMemo, useRef, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {ArrowLeft, ArrowRight} from 'lucide-react'
import {useI18n} from '@/i18n'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServices} from '@/services/context'
import {useAuth} from '@/contexts/auth-context'
import {useStepFocus} from '@/hooks/use-step-focus'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {CONCEPT_ICON} from '@/lib/concepts'
import {PageLayout} from '@/components/shared/PageLayout'
import {WizardStepHeading} from '@/components/shared/WizardStepHeading'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {LoadingState} from '@/components/shared/LoadingState'
import {Icon} from '@/components/shared/Icon'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {Button} from '@/components/ui/button'
import {ErrorBanner} from '@/components/ui/error-banner'
import type {WizardStep as WizardStepType} from '@/components/shared/WizardStepper'
import type {
    CertificationPolicy,
    CreateCertificationPolicyRequest,
    UpdateCertificationPolicyRequest,
} from '@/types/certification'
import {CertificationPolicyResponseFields} from '@/types/certification'
import {WizardStepBasics} from '@/features/certification/components/WizardStepBasics'
import {WizardStepReviewSettings} from '@/features/certification/components/WizardStepReviewSettings'
import {WizardStepScope} from '@/features/certification/components/WizardStepScope'
import {WizardStepLaunch} from '@/features/certification/components/WizardStepLaunch'
import {
    WizardStep,
    useCertificationPolicyWizardForm,
} from '@/features/certification/hooks/useCertificationPolicyWizardForm'

const STEP_PARAM_TO_INDEX: Readonly<Record<string, WizardStep>> = {
    basics: WizardStep.BASICS,
    scope: WizardStep.SCOPE,
    reviewSettings: WizardStep.REVIEW_SETTINGS,
    launch: WizardStep.LAUNCH,
    '0': WizardStep.BASICS,
    '1': WizardStep.SCOPE,
    '2': WizardStep.REVIEW_SETTINGS,
    '3': WizardStep.LAUNCH,
}

export interface CertificationPolicyWizardProps {
    mode?: 'create' | 'edit'
    policyId?: string
}

const WIZARD_STEP_IDS = {
    basics: 'basics',
    scope: 'scope',
    reviewSettings: 'reviewSettings',
    launch: 'launch',
} as const

export function CertificationPolicyWizard({mode = 'create', policyId}: CertificationPolicyWizardProps = {}) {
    const i18n = useI18n()
    const services = useServices()
    const navigate = useNavigate()
    const {hasPermission} = useAuth()
    const {id: idFromParams} = useParams<{id: string}>()
    const [searchParams] = useSearchParams()

    const isEditMode = mode === 'edit'
    const id = policyId ?? idFromParams

    const requiredPermission = isEditMode
        ? permissions.certificationPolicies.update
        : permissions.certificationPolicies.create
    const allowed = hasPermission(requiredPermission)

    const initialStep = useMemo<WizardStep>(() => {
        const raw = searchParams.get('step')
        if (raw && raw in STEP_PARAM_TO_INDEX) return STEP_PARAM_TO_INDEX[raw]
        return WizardStep.BASICS
    }, [searchParams])
    const deepLinked = searchParams.has('step')

    const [currentStep, setCurrentStep] = useState<WizardStep>(initialStep)
    const [policySaved, setPolicySaved] = useState(false)
    const [savedPolicy, setSavedPolicy] = useState<CertificationPolicy | null>(null)
    const [scopePreviewBlocksNext, setScopePreviewBlocksNext] = useState(false)

    const {data: policy, isLoading} = useQuery({
        queryKey: ['certification-policy', id],
        queryFn: () => services.certificationPolicies.detail(id!),
        enabled: allowed && isEditMode && !!id,
    })

    const {form, initialized, setField, validToError, isStepValid, buildBody} =
        useCertificationPolicyWizardForm(isEditMode ? policy : undefined)

    const pageTitle = isEditMode
        ? String(i18n.certificationPolicies.wizard.editPageTitle({name: policy?.name ?? ''}))
        : String(i18n.certificationPolicies.wizard.createPageTitle)
    usePageTitle(pageTitle)

    const createMutation = useEntityMutation({
        mutationFn: (data: CreateCertificationPolicyRequest) =>
            services.certificationPolicies.create(data),
        invalidate: ['certification-policies'],
        successMessage: String(i18n.certificationPolicies.policyCreated),
        onSuccess: (saved: CertificationPolicy) => onPersistSuccess(saved),
    })
    const updateMutation = useEntityMutation({
        mutationFn: (data: UpdateCertificationPolicyRequest) =>
            services.certificationPolicies.update(id!, data),
        invalidate: [['certification-policies'], ['certification-policy', id]],
        successMessage: String(i18n.certificationPolicies.policySaved),
        onSuccess: (saved: CertificationPolicy) => onPersistSuccess(saved),
    })
    const submitMutation = isEditMode ? updateMutation : createMutation

    const returnToSummaryRef = useRef(false)

    function onPersistSuccess(saved: CertificationPolicy) {
        if (returnToSummaryRef.current) {
            navigate(routes.accessGovernance.certificationPolicies.detail(saved.id))
            return
        }
        setPolicySaved(true)
        setSavedPolicy(saved)
        setCurrentStep(WizardStep.LAUNCH)
    }

    const steps: WizardStepType[] = useMemo(() => [
        {id: WIZARD_STEP_IDS.basics, title: String(i18n.certificationPolicies.wizard.stepBasics)},
        {id: WIZARD_STEP_IDS.scope, title: String(i18n.certificationPolicies.wizard.stepScope)},
        {id: WIZARD_STEP_IDS.reviewSettings, title: String(i18n.certificationPolicies.wizard.stepReviewSettings)},
        {id: WIZARD_STEP_IDS.launch, title: String(i18n.certificationPolicies.wizard.stepLaunch)},
    ], [i18n])

    const stepDescriptions = [
        String(i18n.certificationPolicies.wizard.stepBasicsDesc),
        String(i18n.certificationPolicies.wizard.stepScopeDesc),
        String(i18n.certificationPolicies.wizard.stepReviewSettingsDesc),
        String(i18n.certificationPolicies.wizard.stepLaunchDesc),
    ]

    const stepContainerRef = useStepFocus(currentStep, String(steps[currentStep].title))

    const persist = (toSummary: boolean) => {
        returnToSummaryRef.current = toSummary
        const body = buildBody(isEditMode ? policy?.version : undefined)
        submitMutation.mutate(body as never)
    }

    // WHY: SCOPE layers a runtime gate (preview in flight / unresolved server CEL
    // 400) on top of the static step validity — see useCertificationScopePreview
    // and WizardStepScope (spec §5.2, §7.3).
    const canAdvance = (step: WizardStep): boolean => {
        if (!isStepValid(step)) return false
        if (step === WizardStep.SCOPE && scopePreviewBlocksNext) return false
        return true
    }

    const goNext = () => {
        if (!canAdvance(currentStep)) return
        if (currentStep === WizardStep.REVIEW_SETTINGS) {
            persist(false)
            return
        }
        if (currentStep < WizardStep.LAUNCH) {
            setCurrentStep(s => (s + 1) as WizardStep)
        }
    }

    const goBack = () => {
        if (currentStep > WizardStep.BASICS) {
            setCurrentStep(s => (s - 1) as WizardStep)
        } else {
            navigate(-1)
        }
    }

    const onScopePreviewGateChange = useCallback((blockNext: boolean) => {
        setScopePreviewBlocksNext(blockNext)
    }, [])

    // ── Guard: 403 before any fetch ─────────────────────────────────────────
    if (!allowed) {
        return (
            <PageLayout
                data-testid="certification-policy-wizard-page"
                variant="detail"
                title={pageTitle}
                icon={CONCEPT_ICON.certificationCampaign}
                backTo={routes.accessGovernance.certificationPolicies.list()}
            >
                <InfoBanner variant="destructive" data-testid="wizard-forbidden">
                    {i18n.certificationPolicies.wizard.forbidden}
                </InfoBanner>
            </PageLayout>
        )
    }

    // ── Edit-mode hydration: full-page loading then no-flash render ──────────
    if (isEditMode && (isLoading || !initialized)) {
        return <LoadingState message={String(i18n.certificationPolicies.loadingPolicy)}/>
    }

    // ── Default-policy block ─────────────────────────────────────────────────
    if (isEditMode && policy?.isDefault === true) {
        return (
            <PageLayout
                data-testid="certification-policy-wizard-page"
                variant="detail"
                title={pageTitle}
                icon={CONCEPT_ICON.certificationCampaign}
                backTo={routes.accessGovernance.certificationPolicies.list()}
            >
                <InfoBanner variant="info" data-testid="wizard-default-policy-info">
                    {i18n.certificationPolicies.wizard.defaultPolicyInfo}
                </InfoBanner>
            </PageLayout>
        )
    }

    const renderStepContent = () => {
        switch (currentStep) {
            case WizardStep.BASICS:
                return <WizardStepBasics form={form} validToError={validToError} onChange={setField}/>
            case WizardStep.SCOPE:
                return (
                    <WizardStepScope
                        scopeMode={form.scopeMode}
                        conditions={form.conditions}
                        conditionExpr={form.conditionExpr}
                        onScopeModeChange={(mode) => setField('scopeMode', mode)}
                        onConditionsChange={(conditions) => setField(CertificationPolicyResponseFields.conditions, conditions)}
                        onConditionExprChange={(expr) => setField(CertificationPolicyResponseFields.conditionExpr, expr)}
                        onPreviewGateChange={onScopePreviewGateChange}
                    />
                )
            case WizardStep.REVIEW_SETTINGS:
                return <WizardStepReviewSettings form={form} onChange={setField}/>
            case WizardStep.LAUNCH:
                if (!savedPolicy) return null
                return (
                    <WizardStepLaunch
                        form={form}
                        policy={savedPolicy}
                        isEditMode={isEditMode}
                        onBack={() => setCurrentStep(WizardStep.REVIEW_SETTINGS)}
                    />
                )
            default:
                return null
        }
    }

    const isReviewStep = currentStep === WizardStep.REVIEW_SETTINGS
    const isLaunchStep = currentStep === WizardStep.LAUNCH

    const wizardNavigation = isLaunchStep ? null : (
        <>
            <Button variant="outline" onClick={goBack} data-testid="btn-previous-step">
                <Icon icon={ArrowLeft} className="mr-2"/>
                {i18n.certificationPolicies.wizard.back}
            </Button>
            {isReviewStep && isEditMode && deepLinked && (
                <Button
                    variant="ghost"
                    onClick={() => persist(true)}
                    disabled={submitMutation.isPending || !isStepValid(currentStep)}
                    data-testid="btn-save-and-return"
                >
                    {i18n.certificationPolicies.wizard.saveAndReturn}
                </Button>
            )}
            <Button
                onClick={goNext}
                disabled={(isReviewStep && submitMutation.isPending) || !canAdvance(currentStep)}
                data-testid="btn-next-step"
            >
                {isReviewStep && submitMutation.isPending ? (
                    <><ButtonSpinner/>{i18n.common.saving}</>
                ) : (
                    <>
                        {isReviewStep
                            ? i18n.certificationPolicies.wizard.nextToLaunch
                            : i18n.certificationPolicies.wizard.next}
                        <Icon icon={ArrowRight} className="ml-2"/>
                    </>
                )}
            </Button>
        </>
    )

    return (
        <PageLayout
            data-testid="certification-policy-wizard-page"
            variant="wizard"
            title={isEditMode
                ? String(i18n.certificationPolicies.wizard.editTitle({name: policy?.name ?? ''}))
                : String(i18n.certificationPolicies.wizard.createTitle)}
            icon={CONCEPT_ICON.certificationCampaign}
            backTo={true}
            wizardSteps={steps}
            wizardCurrentStep={currentStep}
            wizardNavigation={wizardNavigation}
        >
            <div ref={stepContainerRef} data-testid="certification-policy-wizard-step-container">
                <WizardStepHeading
                    title={steps[currentStep].title}
                    description={stepDescriptions[currentStep]}
                />
                {isReviewStep && submitMutation.error && !policySaved && (
                    <ErrorBanner error={submitMutation.error as Error}/>
                )}
                {renderStepContent()}
            </div>
        </PageLayout>
    )
}
